/**
 * Profile photo. Resizes on a canvas before anything is stored — a 4 MB phone
 * photo lands at roughly 30 KB. SPEC.md §8.
 *
 * TWO PRESENTATIONS, ONE BEHAVIOUR.
 *
 *   <PhotoBox>    the admin member-detail card — avatar, heading, blurb and
 *                 buttons laid out in a row. Unchanged.
 *   <PhotoAvatar> the member's own Edit Profile sheet — just the avatar,
 *                 centred, with a pencil on hover and a menu on click.
 *
 * The edit sheet used to render the admin card verbatim: a heading reading
 * "PROFILE PHOTO", a sentence explaining where the photo appears, and a yellow
 * UPLOAD button, all shoved left inside a bordered box. In a modal that a
 * member opened deliberately to edit their own profile, every word of it was
 * telling them something they had just demonstrated they knew, and the box
 * outweighed the fields underneath it. The avatar alone says "photo"; a pencil
 * says "editable"; the menu says the rest, only once it is asked for.
 *
 * Deliberately NO "Take photo". The WhatsApp menu this borrows from has one,
 * but a camera capture is a second, separate permission prompt and a live
 * video surface to tear down, and the club asked not to have it (owner's
 * direction). `pickAndResizePhoto` opens the OS picker, which on a phone
 * offers the camera anyway.
 */

import { useEffect, useRef, useState } from 'react';
import { initials } from '../data';
import { useClub } from '../context/ClubContext';
import { useUI } from '../context/UIContext';
import { useBackToClose } from '../hooks/useBackToClose';
import { pickAndResizePhoto } from '../lib/image';
import { Button, Icon, Spinner } from './ui';

/**
 * Choosing, uploading and removing — the part both presentations share.
 *
 * `busy` covers the Storage round trip only. The file picker sits open for as
 * long as the person is browsing, and a spinner during that is its own kind of
 * lie, so `pickAndResizePhoto` is awaited outside it.
 */
function usePhotoActions(member) {
  const { db, setPhoto } = useClub();
  const { toast } = useUI();
  const [busy, setBusy] = useState(false);

  /*
   * Resolve the member from club state rather than trusting the prop.
   *
   * Sheets are opened as `openSheet(<ProfileSheet member={me} />)`, which
   * captures the member object as it was at that instant. Uploading or
   * removing a photo updates the club, but that snapshot never hears about it,
   * so the preview inside the sheet kept showing the old photo while the page
   * behind it showed the new one. MemberCreatedSheet already resolves this way
   * for the same reason.
   *
   * Falls back to the prop: an admin looking at a member who is not in the
   * loaded slice still gets something to render.
   */
  const live = db?.members?.find((m) => m.id === member.id) ?? member;

  async function choose() {
    let dataUrl;
    try {
      dataUrl = await pickAndResizePhoto();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not read that photo');
      return;
    }
    if (!dataUrl) return; // picker dismissed

    setBusy(true);
    try {
      await setPhoto(member.id, dataUrl);
      toast('Photo updated');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not update that photo');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await setPhoto(member.id, null);
      toast('Photo removed');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not remove that photo');
    } finally {
      setBusy(false);
    }
  }

  return { member: live, busy, choose, remove };
}

/* ------------------------------------------------------------------ *
 * The member's own edit sheet
 * ------------------------------------------------------------------ */

/** One row of the photo menu. */
function MenuItem({ icon, onClick, tone, children }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-3 py-2 text-left text-[13px] transition ${
        tone === 'danger'
          ? 'text-flag-600 hover:bg-flag-50'
          : 'text-ink-700 hover:bg-line-chip'
      }`}
    >
      <Icon name={icon} size={17} strokeWidth={1.8} className="flex-none opacity-70" />
      {children}
    </button>
  );
}

/**
 * The photo at full size, over everything.
 *
 * z-[100] puts it above the sheet scrim (z-[60]) that it is opened from and
 * below the SOS screen (z-[120]), which must never be covered by anything.
 */
function Lightbox({ src, name, onClose }) {
  useBackToClose(true, onClose);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-ink-900/85 p-6 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`${name}'s profile photo`}
    >
      <img
        src={src}
        alt={`${name}'s profile photo`}
        className="max-h-[80vh] max-w-[min(560px,90vw)] object-contain"
      />
      <button
        type="button"
        onClick={onClose}
        className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
        aria-label="Close"
      >
        <Icon name="close" size={20} strokeWidth={2.2} />
      </button>
    </div>
  );
}

/**
 * Centred avatar, pencil on hover, menu on click.
 *
 * The pencil exists TWICE on purpose. The overlay one appears on hover and is
 * what makes the avatar read as editable on a desktop. The small badge in the
 * corner is always visible, because a phone has no hover state and an
 * affordance nobody can discover is not an affordance — most of this club is
 * on a phone at 6 AM (CLAUDE.md §6).
 */
export function PhotoAvatar({ member: source }) {
  const { member, busy, choose, remove } = usePhotoActions(source);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(false);
  const wrapRef = useRef(null);

  // Close on a click anywhere else, and on Escape. Without the first, the menu
  // survives interaction with the form underneath it and hangs over the fields.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = (fn) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div className="mb-5 flex flex-col items-center">
      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={busy}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Change profile photo"
          className="group relative block h-[88px] w-[88px] overflow-hidden rounded-full border border-line bg-line-chip transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2"
        >
          {member.photo ? (
            <img className="h-full w-full object-cover" src={member.photo} alt="" />
          ) : (
            <span className="grid h-full w-full place-items-center font-market text-[30px] font-extrabold text-copy-label">
              {initials(member.name)}
            </span>
          )}

          {/* Desktop: the whole tile dims and a pencil appears. */}
          <span
            className={`absolute inset-0 grid place-items-center bg-ink-900/60 text-white transition-opacity duration-150 ${
              open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
            }`}
          >
            <Icon name="pencil" size={22} strokeWidth={1.9} />
          </span>

          {busy && (
            <span className="absolute inset-0 grid place-items-center bg-white/75 backdrop-blur-[1px]">
              <Spinner />
            </span>
          )}
        </button>

        {/* Touch: always visible, because there is no hover on a phone. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 right-0 grid h-7 w-7 place-items-center rounded-full border-2 border-white bg-gold-500 text-ink-800"
        >
          <Icon name="pencil" size={13} strokeWidth={2.2} />
        </span>

        {open && (
          <div
            role="menu"
            className="absolute left-1/2 top-full z-[70] mt-3 w-[190px] -translate-x-1/2 border border-line bg-white p-1.5"
          >
            {member.photo && (
              <MenuItem icon="eye" onClick={run(() => setViewing(true))}>
                View photo
              </MenuItem>
            )}
            <MenuItem icon="folder" onClick={run(choose)}>
              {member.photo ? 'Change photo' : 'Upload photo'}
            </MenuItem>
            {member.photo && (
              <>
                <div className="my-1 border-t border-line" />
                <MenuItem icon="trash" tone="danger" onClick={run(remove)}>
                  Remove photo
                </MenuItem>
              </>
            )}
          </div>
        )}
      </div>

      {viewing && member.photo && (
        <Lightbox src={member.photo} name={member.name} onClose={() => setViewing(false)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The admin member-detail card
 * ------------------------------------------------------------------ */

export function PhotoBox({ member: source, blurb }) {
  const { member, busy, choose, remove } = usePhotoActions(source);

  return (
    <div className="flex items-center gap-4 border border-line bg-white p-4">
      <div className="relative grid h-[90px] w-[76px] flex-none place-items-center overflow-hidden border border-line bg-line-chip">
        {member.photo ? (
          <img className="h-full w-full object-cover" src={member.photo} alt="" />
        ) : (
          <div className="font-market text-[28px] font-extrabold text-copy-label">
            {initials(member.name)}
          </div>
        )}
        {/* Over the preview, not beside it — the tile is what changes. */}
        {busy && (
          <div className="absolute inset-0 grid place-items-center bg-white/70 backdrop-blur-[1px]">
            <Spinner />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-market text-base font-bold uppercase tracking-[0.03em]">
          Profile photo
        </div>
        <div className="mt-1 font-market text-[10.5px] leading-relaxed text-copy-muted">
          {blurb ??
            'Appears on your ID card, in the members list and in club chat. Helps the committee and medical staff identify you.'}
        </div>
        <div className="mt-2.5 flex flex-wrap gap-2">
          <Button tone="p" small onClick={choose} disabled={busy}>
            {busy ? 'Uploading…' : member.photo ? 'Change photo' : 'Upload photo'}
          </Button>
          {member.photo && (
            <Button tone="g" small onClick={remove} disabled={busy}>
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
